import { motion } from 'motion/react';
import { useLanguage } from '../contexts/LanguageContext';

interface PDFLoadingOverlayProps {
  isVisible: boolean;
}

export function PDFLoadingOverlay({ isVisible }: PDFLoadingOverlayProps) {
  const { translations: t } = useLanguage();

  if (!isVisible) return null;

  // Safe fallback texts
  const preparingText = t?.results?.preparingPDF || 'Preparing your PDF...';
  const pleaseWaitText = t?.results?.pleaseWait || 'Please wait a moment';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.1, type: "spring", stiffness: 200, damping: 20 }}
        className="relative flex flex-col items-center gap-6 bg-[#1a1b25] px-12 py-10 rounded-2xl shadow-2xl border border-[#90d977]/20"
      >
        {/* Animated spinner with pulse effect */}
        <div className="relative">
          {/* Outer rotating ring */}
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="w-20 h-20 rounded-full border-4 border-[#90d977]/20 border-t-[#90d977]"
          />
          
          {/* Inner pulsing circle */}
          <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.8, 0.5] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            className="absolute inset-0 m-auto w-12 h-12 rounded-full bg-[#90d977]/30"
          />
          
          {/* Center dot */}
          <div className="absolute inset-0 m-auto w-3 h-3 rounded-full bg-[#90d977]" />
        </div>

        {/* Text with stagger animation */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-center"
        >
          <p className="text-white text-lg font-semibold mb-2">
            {preparingText}
          </p>
          <p className="text-[#a1a1aa] text-sm">
            {pleaseWaitText}
          </p>
        </motion.div>

        {/* Animated dots */}
        <div className="flex gap-2">
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              animate={{ y: [0, -8, 0] }}
              transition={{
                duration: 0.6,
                repeat: Infinity,
                delay: i * 0.15,
                ease: "easeInOut",
              }}
              className="w-2 h-2 rounded-full bg-[#90d977]"
            />
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}